import { useCallback, useEffect, useRef } from 'react';
import * as storage from '../../storage/interface';
import { distinctUntilChanged, map } from 'rxjs/operators';
import Sentry from '../../utils/Sentry/Sentry';
import { sessionSubject } from '../../state/sessionObservable';
import { restartApp } from '../../utils/restartApp';

export const DEFAULT_AUTO_RESTART_HOUR = 3;
const HANDLED_DATE_KEY = 'autoRestartHandledDate';
const CONFIGURED_HOUR_KEY = 'autoRestartConfiguredHour';
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
// Runs after the POS restart so both do not hit the server together
const RESTART_OFFSET_MS = 20 * 60 * 1000;

const dateKey = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

export function useAutoRestart(isIdle: () => boolean): () => Promise<void> {
  const isIdleRef = useRef(isIdle);
  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);
  const pendingRef = useRef<string | null>(null);
  const queueRef = useRef<Promise<void>>(Promise.resolve());

  const run = useCallback(async () => {
    const deviceProfile = sessionSubject.getValue()?.device?.deviceProfile;
    if (!deviceProfile) return;

    const restartHour =
      deviceProfile.autoRestartHour ?? DEFAULT_AUTO_RESTART_HOUR;

    const configuredHour = await storage.getItem<number>(CONFIGURED_HOUR_KEY);
    if (configuredHour !== restartHour) {
      await storage.removeItem(HANDLED_DATE_KEY);
      await storage.setItem(CONFIGURED_HOUR_KEY, restartHour);
    }

    const slot = new Date(Date.now() - RESTART_OFFSET_MS);
    const today = dateKey(slot);
    if (
      slot.getHours() === restartHour &&
      (await storage.getItem<string>(HANDLED_DATE_KEY)) !== today
    ) {
      pendingRef.current ??= today;
    }

    // A restart that came due while the display was busy waits for idle, even past the hour
    const pendingDate = pendingRef.current;
    if (!pendingDate || !isIdleRef.current()) return;

    pendingRef.current = null;
    await storage.setItem(HANDLED_DATE_KEY, pendingDate);
    restartApp();
  }, []);

  const check = useCallback(() => {
    queueRef.current = queueRef.current.then(run).catch(error => {
      Sentry.captureException(error);
    });
    return queueRef.current;
  }, [run]);

  useEffect(() => {
    // Emits the current session first, then again when the device profile loads or changes
    const sub = sessionSubject
      .pipe(
        map(session => {
          const profile = session.device?.deviceProfile;
          return (
            profile && (profile.autoRestartHour ?? DEFAULT_AUTO_RESTART_HOUR)
          );
        }),
        distinctUntilChanged(),
      )
      .subscribe(() => void check());
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      sub.unsubscribe();
      clearInterval(id);
    };
  }, [check]);

  return check;
}
